import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Cpu, KeyRound, Lock, Plus, Trash2 } from 'lucide-react';
import {
    deleteTenantConfig,
    deleteTenantLimits,
    deleteTenantSecret,
    deleteTenantSecretValue,
    getTenantLimits,
    listTenantConfig,
    listTenantSecrets,
    listTenantSecretValueKeys,
    setTenantConfig,
    setTenantLimits,
    setTenantSecret,
    setTenantSecretValue,
    type TenantConfigValue,
    type TenantLimitsOverride,
    type TenantSecretRef,
} from '@/lib/tenant-overrides';

// DeploymentTab lets an admin manage tenant-scoped execution overrides for a
// capability (plan 46 Phase 5): public config values, secret indirections, and
// resource limit overrides. The tab intentionally doesn't let the admin enter
// a secret value — only the provider + ref pointer — so secrets never land in
// the registry DB.

interface DeploymentTabProps {
    cap: string;
}

export function DeploymentTab({ cap }: DeploymentTabProps) {
    if (!cap) {
        return (
            <Card className="p-12 text-center">
                <p className="text-sm text-slate-500">No capability selected.</p>
            </Card>
        );
    }
    return (
        <div className="flex flex-col gap-8">
            <LimitsSection cap={cap} />
            <ConfigSection cap={cap} />
            <SecretsSection cap={cap} />
        </div>
    );
}

// -----------------------------------------------------------------------------
// Limits
// -----------------------------------------------------------------------------

function LimitsSection({ cap }: { cap: string }) {
    const [limits, setLimits] = useState<TenantLimitsOverride>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getTenantLimits({ cap });
            setLimits(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [cap]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await setTenantLimits({
                cap,
                timeoutMs: limits.timeoutMs,
                memoryMb: limits.memoryMb,
                cpuMillicores: limits.cpuMillicores,
            });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const onClear = async () => {
        setSaving(true);
        setError(null);
        try {
            await deleteTenantLimits({ cap });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="p-6">
            <header className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                        <Cpu size={20} className="text-amber-600" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-slate-900">Resource limits</h3>
                        <p className="text-sm text-slate-500">
                            Tenant-specific timeout and resource caps. Policy envelopes and manifest
                            bounds still apply.
                        </p>
                    </div>
                </div>
            </header>

            {error && (
                <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-3 gap-4">
                <LimitInput
                    label="Timeout (ms)"
                    value={limits.timeoutMs ?? null}
                    onChange={(v) => setLimits((prev) => ({ ...prev, timeoutMs: v }))}
                />
                <LimitInput
                    label="Memory (MB)"
                    value={limits.memoryMb ?? null}
                    onChange={(v) => setLimits((prev) => ({ ...prev, memoryMb: v }))}
                />
                <LimitInput
                    label="CPU (millicores)"
                    value={limits.cpuMillicores ?? null}
                    onChange={(v) => setLimits((prev) => ({ ...prev, cpuMillicores: v }))}
                />
            </div>

            <div className="mt-4 flex items-center gap-2">
                <Button onClick={onSave} disabled={loading || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button variant="outline" onClick={onClear} disabled={loading || saving}>
                    Clear override
                </Button>
                {limits.modified && (
                    <span className="ml-auto text-xs text-slate-500">
                        Last updated {formatDate(limits.modified)}
                    </span>
                )}
            </div>
        </Card>
    );
}

function LimitInput({
    label,
    value,
    onChange,
}: {
    label: string;
    value: number | null;
    onChange: (v: number | null) => void;
}) {
    return (
        <div>
            <Label className="text-xs font-medium text-slate-600">{label}</Label>
            <Input
                type="number"
                min={0}
                value={value ?? ''}
                onChange={(e) => {
                    const raw = e.target.value;
                    onChange(raw === '' ? null : Number(raw));
                }}
                className="mt-1"
            />
        </div>
    );
}

// -----------------------------------------------------------------------------
// Public config
// -----------------------------------------------------------------------------

function ConfigSection({ cap }: { cap: string }) {
    const [values, setValues] = useState<TenantConfigValue[]>([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setValues(await listTenantConfig({ cap }));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [cap]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onAdd = async () => {
        if (!newKey || !newValue) return;
        setBusy(true);
        setError(null);
        try {
            await setTenantConfig({ cap, key: newKey, value: newValue });
            setNewKey('');
            setNewValue('');
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (key: string) => {
        setBusy(true);
        setError(null);
        try {
            await deleteTenantConfig({ cap, key });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="p-6">
            <header className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100">
                    <KeyRound size={20} className="text-sky-600" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-slate-900">Config values</h3>
                    <p className="text-sm text-slate-500">
                        Public environment variables applied at invocation. Values are stored in
                        plaintext — do not put credentials here.
                    </p>
                </div>
            </header>

            {error && (
                <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <div className="mb-4 flex items-end gap-2">
                <div className="flex-1">
                    <Label className="text-xs font-medium text-slate-600">Key</Label>
                    <Input
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                        placeholder="LOG_LEVEL"
                        pattern="[A-Z_][A-Z0-9_]*"
                        className="mt-1 font-mono"
                    />
                </div>
                <div className="flex-1">
                    <Label className="text-xs font-medium text-slate-600">Value</Label>
                    <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder="info"
                        className="mt-1"
                    />
                </div>
                <Button onClick={onAdd} disabled={busy || !newKey || !newValue}>
                    <Plus size={16} className="mr-1" />
                    Add
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-slate-500">Loading…</p>
            ) : values.length === 0 ? (
                <p className="text-sm text-slate-500">No tenant config values set.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {values.map((v) => (
                        <li key={v.key} className="flex items-center justify-between py-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm font-semibold text-slate-800">{v.key}</span>
                                    <Badge variant="secondary" className="text-[10px]">
                                        config
                                    </Badge>
                                </div>
                                <p className="mt-1 truncate font-mono text-xs text-slate-500">{v.value}</p>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => onDelete(v.key)}
                                className="text-rose-600 hover:text-rose-700"
                            >
                                <Trash2 size={14} />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

// -----------------------------------------------------------------------------
// Secrets
// -----------------------------------------------------------------------------

function SecretsSection({ cap }: { cap: string }) {
    const [secrets, setSecrets] = useState<TenantSecretRef[]>([]);
    const [valueKeys, setValueKeys] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newKey, setNewKey] = useState('');
    const [newProvider, setNewProvider] = useState('env');
    const [newRef, setNewRef] = useState('');
    const [newField, setNewField] = useState('');
    const [editingValueKey, setEditingValueKey] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [refs, keys] = await Promise.all([
                listTenantSecrets({ cap }),
                listTenantSecretValueKeys({ cap }),
            ]);
            setSecrets(refs);
            setValueKeys(new Set(keys));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [cap]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const onAdd = async () => {
        if (!newKey || !newProvider || !newRef) return;
        setBusy(true);
        setError(null);
        try {
            await setTenantSecret({
                cap,
                key: newKey,
                provider: newProvider,
                ref: newRef,
                field: newField || undefined,
            });
            setNewKey('');
            setNewRef('');
            setNewField('');
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (key: string) => {
        setBusy(true);
        setError(null);
        try {
            await deleteTenantSecret({ cap, key });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const onSetValue = async (key: string) => {
        if (!editingValue.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await setTenantSecretValue({ cap, key, value: editingValue });
            setEditingValueKey(null);
            setEditingValue('');
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const onDeleteValue = async (key: string) => {
        setBusy(true);
        setError(null);
        try {
            await deleteTenantSecretValue({ cap, key });
            await refresh();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="p-6">
            <header className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100">
                    <Lock size={20} className="text-rose-600" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-slate-900">Secret references</h3>
                    <p className="text-sm text-slate-500">
                        Secret indirections and values. Values are encrypted at rest and
                        resolved by the worker at invocation time.
                    </p>
                </div>
            </header>

            {error && (
                <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <div className="mb-4 grid grid-cols-4 items-end gap-2">
                <div>
                    <Label className="text-xs font-medium text-slate-600">Key</Label>
                    <Input
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value.toUpperCase())}
                        placeholder="OPENAI_API_KEY"
                        pattern="[A-Z_][A-Z0-9_]*"
                        className="mt-1 font-mono"
                    />
                </div>
                <div>
                    <Label className="text-xs font-medium text-slate-600">Provider</Label>
                    <Input
                        value={newProvider}
                        onChange={(e) => setNewProvider(e.target.value)}
                        placeholder="env"
                        className="mt-1"
                    />
                </div>
                <div>
                    <Label className="text-xs font-medium text-slate-600">Ref</Label>
                    <Input
                        value={newRef}
                        onChange={(e) => setNewRef(e.target.value)}
                        placeholder="OPENAI_API_KEY"
                        className="mt-1 font-mono"
                    />
                </div>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Label className="text-xs font-medium text-slate-600">Field (optional)</Label>
                        <Input
                            value={newField}
                            onChange={(e) => setNewField(e.target.value)}
                            placeholder="password"
                            className="mt-1"
                        />
                    </div>
                    <Button onClick={onAdd} disabled={busy || !newKey || !newRef}>
                        <Plus size={16} className="mr-1" />
                        Add
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-slate-500">Loading…</p>
            ) : secrets.length === 0 ? (
                <p className="text-sm text-slate-500">No secret references set.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {secrets.map((s) => {
                        const hasValue = valueKeys.has(s.key);
                        const isEditing = editingValueKey === s.key;
                        return (
                            <li key={s.key} className="py-3">
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-sm font-semibold text-slate-800">{s.key}</span>
                                            <Badge variant="secondary" className="text-[10px]">
                                                {s.provider}
                                            </Badge>
                                            {hasValue && (
                                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                                                    value set
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="mt-1 truncate font-mono text-xs text-slate-500">
                                            {s.ref}
                                            {s.field ? ` · field:${s.field}` : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={busy}
                                            onClick={() => {
                                                if (isEditing) {
                                                    setEditingValueKey(null);
                                                    setEditingValue('');
                                                } else {
                                                    setEditingValueKey(s.key);
                                                    setEditingValue('');
                                                }
                                            }}
                                        >
                                            {isEditing ? 'Cancel' : hasValue ? 'Update value' : 'Set value'}
                                        </Button>
                                        {hasValue && !isEditing && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                disabled={busy}
                                                onClick={() => onDeleteValue(s.key)}
                                                className="text-amber-600 hover:text-amber-700"
                                            >
                                                Clear value
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={busy}
                                            onClick={() => onDelete(s.key)}
                                            className="text-rose-600 hover:text-rose-700"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                </div>
                                {isEditing && (
                                    <div className="mt-2 flex items-end gap-2">
                                        <div className="flex-1">
                                            <Label className="text-xs font-medium text-slate-600">Secret value</Label>
                                            <Input
                                                type="password"
                                                value={editingValue}
                                                onChange={(e) => setEditingValue(e.target.value)}
                                                placeholder="Enter secret value…"
                                                className="mt-1 font-mono"
                                                autoFocus
                                            />
                                        </div>
                                        <Button
                                            onClick={() => onSetValue(s.key)}
                                            disabled={busy || !editingValue.trim()}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
}

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}
